import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Phone, 
  ArrowRight, 
  Droplets,
  Disc,
  Cpu,
  Cog,
  Gauge,
  Thermometer,
  Battery,
  Settings,
  CheckCircle
} from "lucide-react";

const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";

const services = [
  { 
    icon: Droplets, 
    name: "Oil Changes", 
    description: "Regular oil changes are essential for engine health. We use quality oils and filters to keep your engine running smooth.",
    features: ["Conventional & synthetic oils", "Filter replacement", "Fluid top-off"],
    image: "https://images.unsplash.com/photo-1771340742493-52fbd5476ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjYXIlMjBvaWwlMjBjaGFuZ2UlMjBtZWNoYW5pY3xlbnwwfHx8fDE3NzIwNTI4MDR8MA&ixlib=rb-4.1.0&q=85"
  },
  { 
    icon: Disc, 
    name: "Brake Repair", 
    description: "Your safety depends on reliable brakes. We inspect, repair, and replace brake components to ensure you stop safely.",
    features: ["Pad & rotor replacement", "Brake fluid flush", "ABS diagnostics"],
    image: "https://images.unsplash.com/photo-1764869427601-eb699ac80a0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjYXIlMjBicmFrZSUyMHJlcGFpciUyMG1lY2hhbmljfGVufDB8fHx8MTc3MjA1MjgwNXww&ixlib=rb-4.1.0&q=85"
  },
  { 
    icon: Cpu, 
    name: "Engine Diagnostics", 
    description: "Check engine light on? Our advanced diagnostic tools pinpoint problems accurately so we fix it right the first time.",
    features: ["Computer diagnostics", "Error code reading", "Performance testing"],
    image: "https://images.unsplash.com/photo-1770656506122-99bc73ac9d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjYXIlMjBlbmdpbmUlMjBkaWFnbm9zdGljcyUyMG1lY2hhbmljfGVufDB8fHx8MTc3MjA1MjgwN3ww&ixlib=rb-4.1.0&q=85"
  },
  { 
    icon: Cog, 
    name: "Transmission Service", 
    description: "Whether it's a fluid change or a major repair, our experts handle all transmission needs with precision.",
    features: ["Fluid exchange", "Filter replacement", "Repair & rebuild"],
    image: "https://images.pexels.com/photos/4488640/pexels-photo-4488640.jpeg"
  },
  { 
    icon: Gauge, 
    name: "Tune-Ups", 
    description: "Keep your vehicle running at peak performance with comprehensive tune-up services.",
    features: ["Spark plug replacement", "Air filter change", "System inspection"],
    image: "https://images.unsplash.com/photo-1770656506122-99bc73ac9d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjYXIlMjBlbmdpbmUlMjBkaWFnbm9zdGljcyUyMG1lY2hhbmljfGVufDB8fHx8MTc3MjA1MjgwN3ww&ixlib=rb-4.1.0&q=85"
  },
  { 
    icon: Thermometer, 
    name: "AC & Heating Repair", 
    description: "Stay comfortable in any weather. We service all heating and cooling systems.",
    features: ["A/C recharge", "Heater core repair", "Climate control"],
    image: "https://images.pexels.com/photos/4488640/pexels-photo-4488640.jpeg"
  },
  { 
    icon: Battery, 
    name: "Battery Replacement", 
    description: "Don't get stranded with a dead battery. We test and replace batteries for reliable starts.",
    features: ["Battery testing", "New battery install", "Terminal cleaning"],
    image: "https://images.unsplash.com/photo-1771340742493-52fbd5476ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjYXIlMjBvaWwlMjBjaGFuZ2UlMjBtZWNoYW5pY3xlbnwwfHx8fDE3NzIwNTI4MDR8MA&ixlib=rb-4.1.0&q=85"
  },
  { 
    icon: Settings, 
    name: "General Auto Maintenance", 
    description: "From inspections to minor repairs, we handle all your routine vehicle maintenance needs.",
    features: ["Multi-point inspection", "Fluid checks", "Belt & hose inspection"],
    image: "https://images.pexels.com/photos/4488640/pexels-photo-4488640.jpeg"
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-foreground mb-6 animate-fade-in-up">
              Our <span className="text-primary">Services</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up delay-100">
              From routine oil changes to complex engine repairs, Tony's Auto Quality Service 
              has the expertise to keep your vehicle running safely and reliably.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={service.name}
                data-testid={`service-detail-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="service-card bg-card border-border overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-2xl uppercase text-white">
                      {service.name}
                    </h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-6">
            Need a <span className="text-primary">Service?</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Call us today or request a quote online. We'll get your vehicle back in top shape.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK} data-testid="services-call-btn">
              <Button className="cta-primary flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </Button>
            </a>
            <Link to="/quote" data-testid="services-quote-btn">
              <Button className="cta-secondary flex items-center gap-2">
                Request a Quote
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
